import { useState, useEffect } from "react";
import api from "../../services/api";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faSquarePlus, faSquareMinus } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { usePatologia } from "../../context/PatologiaContext";
import RuleBuilder from "./RuleBuilder";

const Reglas = () => {
  const [reglas, setReglas] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState("activas");
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const { patologiaActiva } = usePatologia();
  const idPatologia = localStorage.getItem("idPatologiaSeleccionada");

  useEffect(() => {
    if (idPatologia) {
      getReglas();
    }
  }, [idPatologia]);

  const getReglas = async () => {
    try {
      const response = await api.get(`/reglas/patologia/${idPatologia}`);
      setReglas(response.data);
    } catch (error) {
      console.error("Error al obtener reglas:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las reglas.",
      });
    }
  };

  const handleCreateOrUpdateRule = async (ruleData) => {
    try {
      if (editingRule) {
        // Actualizar regla existente
        await api.put(`/api/reglas/${editingRule.IdRegla}`, ruleData);
        Swal.fire({
          icon: "success",
          title: "Regla actualizada",
          text: "La regla se actualizó correctamente.",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        // Crear nueva regla
        await api.post(`/api/reglas`, {
          ...ruleData,
          IdPatologia: parseInt(idPatologia),
        });
        Swal.fire({
          icon: "success",
          title: "Regla creada",
          text: "La regla se creó correctamente.",
          timer: 1500,
          showConfirmButton: false,
        });
      }
      setShowForm(false);
      setEditingRule(null);
      getReglas();
    } catch (error) {
      console.error("Error al guardar regla:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo guardar la regla.",
      });
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleToggleEstado = async (IdRegla) => {
    try {
      await api.patch(`/api/reglas/toggle/${IdRegla}`);
      Swal.fire({
        icon: "success",
        title: "Estado actualizado",
        text: "El estado de la regla se actualizó correctamente.",
        timer: 1500,
        showConfirmButton: false,
      });
      getReglas();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cambiar el estado de la regla.",
      });
    }
  };

  const filtrarReglas = () => {
    if (filtroEstado === "activas") {
      return reglas.filter((r) => r.Activo === 1);
    }
    if (filtroEstado === "inactivas") {
      return reglas.filter((r) => r.Activo === 0);
    }
    return reglas;
  };

  const columnas = [
    {
      name: "Nombre",
      selector: (row) => row.Nombre,
      sortable: true,
      width: "20%",
    },
    {
      name: "Descripción",
      selector: (row) => row.Descripcion || "-",
      sortable: true,
      width: "30%",
    },
    {
      name: "Estado",
      selector: (row) => (row.Activo === 1 ? "Activa" : "Inactiva"),
      sortable: true,
      width: "10%",
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            className="button is-small is-info"
            onClick={() => handleEdit(row)}
            title="Editar"
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
          <button
            className={`button is-small ${row.Activo === 1 ? "is-danger" : "is-success"}`}
            onClick={() => handleToggleEstado(row.IdRegla)}
            title={row.Activo === 1 ? "Desactivar" : "Activar"}
          >
            <FontAwesomeIcon icon={row.Activo === 1 ? faSquareMinus : faSquarePlus} />
          </button>
        </div>
      ),
      width: "15%",
    },
  ];

  if (!idPatologia) {
    return (
      <div className="container mt-5">
        <div className="notification is-warning">
          <p>Por favor, selecciona una patología activa para gestionar las reglas.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="columns">
        <div className="column">
          <h1 className="title">Reglas - {patologiaActiva}</h1>
        </div>
      </div>

      {!showForm ? (
        <>
          <div className="columns">
            <div className="column">
              <button
                className="button is-primary"
                onClick={() => {
                  setEditingRule(null);
                  setShowForm(true);
                }}
              >
                <FontAwesomeIcon icon={faSquarePlus} style={{ marginRight: "8px" }} />
                Nueva Regla
              </button>
            </div>
            <div className="column is-narrow">
              <div className="select">
                <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                  <option value="todas">Todas</option>
                  <option value="activas">Activas</option>
                  <option value="inactivas">Inactivas</option>
                </select>
              </div>
            </div>
          </div>

          <div className="box">
            <DataTable
              columns={columnas}
              data={filtrarReglas()}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 30]}
              highlightOnHover
              striped
              responsive
              noDataComponent="No hay reglas para mostrar"
            />
          </div>
        </>
      ) : (
        <div className="box">
          <div className="columns">
            <div className="column">
              <h2 className="subtitle">{editingRule ? "Editar Regla" : "Nueva Regla"}</h2>
            </div>
            <div className="column is-narrow">
              <button
                className="button is-light"
                onClick={() => {
                  setShowForm(false);
                  setEditingRule(null);
                }}
              >
                Cancelar
              </button>
            </div>
          </div>

          <RuleBuilder
            idPatologia={idPatologia}
            editingRule={editingRule}
            onSave={handleCreateOrUpdateRule}
            onCancel={() => {
              setShowForm(false);
              setEditingRule(null);
            }}
            idPersona={localStorage.getItem("idPersonaSeleccionada")}
          />
        </div>
      )}
    </div>
  );
};

export default Reglas;
